import React, {
    createContext,
    useContext,
    useState,
    useRef,
    useEffect,
    useCallback,
} from "react";
import { useMediaPermission } from "./MediaPermissionContext";
import { useLocation } from "react-router-dom";

interface CameraContextType {
    videoRef: React.RefObject<HTMLVideoElement | null>;
    cameraActive: boolean;
    hasPermission: boolean;
    toggleCamera: () => void;
    isInitialized: boolean;
}

const CameraContext = createContext<CameraContextType | undefined>(undefined);

export const CameraProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { permissions, stream } = useMediaPermission();
    const [cameraActive, setCameraActive] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const videoStreamRef = useRef<MediaStream | null>(null);
    const activeRef = useRef(true);
    const playAttemptInProgress = useRef(false);
    const location = useLocation();

    // Function to check if the current route should allow camera
    const isAllowedRoute = useCallback(() => {
        return (
            location.pathname.includes("/mock-case") ||
            location.pathname.includes("/report") ||
            location.pathname.includes("/case-mock")
        );
    }, [location.pathname]);

    // Add event listeners to the video element once it's available
    useEffect(() => {
        if (videoRef.current) {
            const videoElement = videoRef.current;

            const handlePlaying = () =>
                console.log("Video playing event fired");
            const handlePause = () => console.log("Video paused event fired");
            const handleError = (e: Event) =>
                console.error("Video error event:", e);
            const handleStalled = () =>
                console.log("Video stalled event fired");

            videoElement.addEventListener("playing", handlePlaying);
            videoElement.addEventListener("pause", handlePause);
            videoElement.addEventListener("error", handleError);
            videoElement.addEventListener("stalled", handleStalled);

            return () => {
                videoElement.removeEventListener("playing", handlePlaying);
                videoElement.removeEventListener("pause", handlePause);
                videoElement.removeEventListener("error", handleError);
                videoElement.removeEventListener("stalled", handleStalled);
            };
        }
    }, [videoRef.current]);

    // Toggle camera state with a callback to ensure latest state
    const toggleCamera = useCallback(() => {
        console.log("Toggle camera called, current state:", activeRef.current);

        // Don't allow toggling until initialized
        if (!isInitialized) {
            console.log("Toggle ignored - camera not yet initialized");
            return;
        }

        setCameraActive((prevActive) => {
            const newActive = !prevActive;
            console.log(`Camera toggled: ${prevActive} -> ${newActive}`);
            activeRef.current = newActive;
            return newActive;
        });
    }, [isInitialized]);

    // Apply camera state changes
    const applyCameraState = useCallback(() => {
        console.log(`Applying camera state: active=${activeRef.current}`);

        if (!videoRef.current) {
            console.log("Video ref not ready");
            return;
        }

        if (!stream) {
            console.log("No media stream available");
            return;
        }

        const videoTracks = stream.getVideoTracks();
        console.log(`Found ${videoTracks.length} video tracks`);

        if (videoTracks.length === 0) {
            console.log("No video tracks available");
            return;
        }

        try {
            if (activeRef.current && permissions.video) {
                console.log("Enabling camera");

                // Ensure all tracks are enabled
                videoTracks.forEach((track) => {
                    track.enabled = true;
                    console.log(`Enabled track: ${track.id}`);
                });

                // Create a new media stream only if needed
                if (!videoStreamRef.current) {
                    videoStreamRef.current = new MediaStream(videoTracks);
                    console.log("Created new video stream");
                }

                // Safely assign stream to video element
                if (videoRef.current) {
                    console.log("Setting video element properties");

                    // Set video properties
                    videoRef.current.muted = true; // Mute to avoid feedback
                    videoRef.current.playsInline = true; // Better for mobile
                    videoRef.current.autoplay = true; // Try autoplay
                    videoRef.current.style.transform = "scaleX(-1)"; // Mirror

                    // Only set srcObject if needed
                    if (videoRef.current.srcObject !== videoStreamRef.current) {
                        console.log("Setting new srcObject");
                        videoRef.current.srcObject = videoStreamRef.current;

                        // Force play with user interaction handling
                        if (!playAttemptInProgress.current) {
                            playAttemptInProgress.current = true;
                            console.log("Attempting to play video");

                            // Use requestAnimationFrame for better timing
                            requestAnimationFrame(() => {
                                if (
                                    videoRef.current &&
                                    videoRef.current.srcObject
                                ) {
                                    const playPromise = videoRef.current.play();

                                    if (playPromise !== undefined) {
                                        playPromise
                                            .then(() => {
                                                console.log(
                                                    "Video playing successfully"
                                                );
                                            })
                                            .catch((err) => {
                                                console.error(
                                                    "Error playing video:",
                                                    err
                                                );

                                                // If we get a user interaction error, we'll try once more when the video becomes ready
                                                if (
                                                    err.name ===
                                                    "NotAllowedError"
                                                ) {
                                                    console.log(
                                                        "Adding loadedmetadata listener for play retry"
                                                    );
                                                    videoRef.current?.addEventListener(
                                                        "loadedmetadata",
                                                        function playOnLoad() {
                                                            console.log(
                                                                "Video metadata loaded, trying play again"
                                                            );
                                                            videoRef.current
                                                                ?.play()
                                                                .catch((e) =>
                                                                    console.warn(
                                                                        "Second play attempt failed:",
                                                                        e
                                                                    )
                                                                );
                                                            videoRef.current?.removeEventListener(
                                                                "loadedmetadata",
                                                                playOnLoad
                                                            );
                                                        }
                                                    );
                                                }
                                            })
                                            .finally(() => {
                                                playAttemptInProgress.current =
                                                    false;
                                            });
                                    } else {
                                        console.log(
                                            "Play returned undefined (likely IE)"
                                        );
                                        playAttemptInProgress.current = false;
                                    }
                                } else {
                                    console.log(
                                        "Video ref or srcObject no longer available"
                                    );
                                    playAttemptInProgress.current = false;
                                }
                            });
                        }
                    } else {
                        console.log("Video srcObject already set correctly");

                        // Check if video is actually playing
                        if (videoRef.current.paused) {
                            console.log("Video is paused, attempting to play");
                            videoRef.current.play().catch((err) => {
                                console.warn(
                                    "Play attempt on existing video failed:",
                                    err
                                );
                            });
                        } else {
                            console.log("Video is already playing");
                        }
                    }
                }
            } else {
                console.log("Disabling camera");
                videoTracks.forEach((track) => {
                    track.enabled = false;
                    console.log(`Disabled track: ${track.id}`);
                });

                if (videoRef.current) {
                    videoRef.current.pause();
                    videoRef.current.srcObject = null;
                    console.log("Video paused and srcObject cleared");
                }

                videoStreamRef.current = null;
            }

            // Mark as initialized after first successful setup
            if (!isInitialized) {
                setIsInitialized(true);
                console.log("Camera initialized");
            }
        } catch (error) {
            console.error("Error in camera state application:", error);
            playAttemptInProgress.current = false;
        }
    }, [stream, permissions.video, isInitialized]);

    // Watch for camera active state changes
    useEffect(() => {
        console.log(`Camera active state changed: ${cameraActive}`);
        if (isAllowedRoute()) {
            applyCameraState();
        } else {
            // Force camera off for non-allowed routes
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.srcObject = null;
                console.log("Video disabled on non-allowed route");
            }

            // Disable any video tracks
            if (stream) {
                const videoTracks = stream.getVideoTracks();
                videoTracks.forEach((track) => {
                    track.enabled = false;
                });
            }

            videoStreamRef.current = null;
        }
    }, [cameraActive, applyCameraState, isAllowedRoute, stream]);

    // Also watch for stream changes
    useEffect(() => {
        console.log("Stream or permissions changed");
        if (stream && videoRef.current && isAllowedRoute()) {
            applyCameraState();
        }
    }, [stream, permissions.video, applyCameraState, isAllowedRoute]);

    // Also watch for route changes
    useEffect(() => {
        if (!isAllowedRoute()) {
            // Turn off camera if navigating to a restricted route
            if (cameraActive) {
                console.log("Navigated to restricted route, disabling camera");
                setCameraActive(false);
                activeRef.current = false;
            }
        }
    }, [location.pathname, isAllowedRoute, cameraActive]);

    // Clean up when component unmounts
    useEffect(() => {
        return () => {
            console.log("Camera context unmounting, cleaning up");
            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }
            videoStreamRef.current = null;
        };
    }, []);

    const contextValue = {
        videoRef,
        cameraActive,
        hasPermission: !!permissions.video,
        toggleCamera,
        isInitialized,
    };

    return (
        <CameraContext.Provider value={contextValue}>
            {children}
        </CameraContext.Provider>
    );
};

export const useCamera = () => {
    const context = useContext(CameraContext);
    if (context === undefined) {
        throw new Error("useCamera must be used within a CameraProvider");
    }
    return context;
};
